import { useLanguage } from '../contexts/LanguageContext';
import { Shield, Eye, Database, Lock, Globe, Bell, UserCheck, Mail } from 'lucide-react';

export default function PrivacyPage() {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Privacy Policy',
      lastUpdated: 'Last Updated: December 2024',
      intro: 'PEP-T.COM ("we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and purchase our products.',
      sections: [
        {
          icon: Database,
          title: 'Information We Collect',
          content: `**Personal Information**
When you visit our website or make a purchase, we may collect the following personal information:

• Name and contact details (email address, phone number)
• Billing and shipping address
• Company or institution name
• Payment information (processed securely by third-party payment processors)
• Order history and preferences

**Automatically Collected Information**
We automatically collect certain information when you visit our website:

• IP address and geographic location
• Browser type and version
• Operating system
• Pages viewed and time spent on site
• Referring website addresses
• Device information

**Cookies and Tracking Technologies**
We use cookies and similar technologies to:

• Remember your preferences and settings
• Analyze website traffic and usage patterns
• Improve website functionality
• Provide personalized content`,
        },
        {
          icon: Eye,
          title: 'How We Use Your Information',
          content: `We use the information we collect for the following purposes:

**Order Fulfillment**
• Process and fulfill your orders
• Send order confirmations and shipping notifications
• Provide customer support

**Business Operations**
• Improve our website and services
• Analyze usage trends and preferences
• Develop new products and features
• Prevent fraud and unauthorized access

**Communications**
• Respond to your inquiries and requests
• Send important updates about your orders
• Provide information about products (with your consent)

**Legal Compliance**
• Comply with applicable laws and regulations
• Respond to legal requests and prevent harm
• Enforce our Terms and Conditions`,
        },
        {
          icon: Lock,
          title: 'Information Security',
          content: `We implement appropriate technical and organizational measures to protect your personal information:

**Security Measures**
• SSL/TLS encryption for data transmission
• Secure payment processing through PCI-compliant providers
• Access controls and authentication
• Regular security assessments
• Employee training on data protection

**Data Retention**
We retain your personal information only for as long as necessary to:
• Fulfill the purposes outlined in this policy
• Comply with legal obligations
• Resolve disputes and enforce agreements

Order records are retained for a minimum of 7 years for legal and accounting purposes.

**Note**: While we strive to protect your information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.`,
        },
        {
          icon: Globe,
          title: 'Information Sharing',
          content: `We do not sell your personal information. We may share your information with:

**Service Providers**
• Payment processors
• Shipping and logistics companies
• IT and hosting providers
• Analytics services

**Legal Requirements**
We may disclose your information if required by law or in response to:
• Court orders or legal processes
• Government requests
• Protection of our rights and property
• Prevention of fraud or illegal activities

**Business Transfers**
In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.`,
        },
        {
          icon: UserCheck,
          title: 'Your Rights',
          content: `Depending on your location, you may have the following rights regarding your personal information:

**Access and Portability**
• Request a copy of your personal data
• Receive your data in a portable format

**Correction and Deletion**
• Request correction of inaccurate data
• Request deletion of your data (subject to legal obligations)

**Opt-Out Rights**
• Unsubscribe from marketing communications
• Disable cookies through your browser settings
• Opt out of analytics tracking

**Exercise Your Rights**
To exercise any of these rights, please contact us at support@pep-t.com. We will respond to your request within 30 days.`,
        },
        {
          icon: Bell,
          title: 'Communications and Marketing',
          content: `**Transactional Communications**
We will send you emails related to your orders, including:
• Order confirmations
• Shipping updates
• Important account notifications

These communications are necessary for order fulfillment and cannot be opted out.

**Marketing Communications**
With your consent, we may send promotional emails about:
• New products
• Special offers
• Industry news

You can unsubscribe from marketing emails at any time by:
• Clicking the "unsubscribe" link in any email
• Contacting us at support@pep-t.com`,
        },
        {
          icon: Shield,
          title: 'International Data Transfers',
          content: `As an export-only business, your information may be transferred to and processed in:
• United States (where our servers are located)
• Countries where our service providers operate

We ensure appropriate safeguards are in place for international transfers, including:
• Standard contractual clauses
• Data protection agreements
• Compliance with applicable data protection laws

By using our website and services, you consent to the transfer of your information to these countries.`,
        },
      ],
      children: {
        title: 'Children\'s Privacy',
        content: 'Our website and services are not intended for individuals under the age of 21. We do not knowingly collect personal information from minors. If we become aware that we have collected information from a person under 21, we will delete that information promptly.',
      },
      changes: {
        title: 'Changes to This Policy',
        content: 'We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of our website after such changes constitutes acceptance of the updated policy.',
      },
      contact: {
        title: 'Contact Us',
        text: 'If you have questions about this Privacy Policy or our data practices, please contact us:',
        email: 'support@pep-t.com',
        location: 'Boca Raton, Florida, USA',
      },
    },
    pt: {
      title: 'Política de Privacidade',
      lastUpdated: 'Última Atualização: Dezembro 2024',
      intro: 'A PEP-T.COM ("nós", "nos" ou "nosso") está comprometida em proteger sua privacidade. Esta Política de Privacidade explica como coletamos, usamos, divulgamos e protegemos suas informações quando você visita nosso site e compra nossos produtos.',
      sections: [
        {
          icon: Database,
          title: 'Informações que Coletamos',
          content: `**Informações Pessoais**
Quando você visita nosso site ou faz uma compra, podemos coletar as seguintes informações pessoais:

• Nome e detalhes de contato (endereço de e-mail, número de telefone)
• Endereço de cobrança e entrega
• Nome da empresa ou instituição
• Informações de pagamento (processadas com segurança por processadores de pagamento terceiros)
• Histórico de pedidos e preferências

**Informações Coletadas Automaticamente**
Coletamos automaticamente certas informações quando você visita nosso site:

• Endereço IP e localização geográfica
• Tipo e versão do navegador
• Sistema operacional
• Páginas visualizadas e tempo gasto no site
• Endereços de sites de referência
• Informações do dispositivo

**Cookies e Tecnologias de Rastreamento**
Usamos cookies e tecnologias similares para:

• Lembrar suas preferências e configurações
• Analisar o tráfego e padrões de uso do site
• Melhorar a funcionalidade do site
• Fornecer conteúdo personalizado`,
        },
        {
          icon: Eye,
          title: 'Como Usamos Suas Informações',
          content: `Usamos as informações coletadas para os seguintes propósitos:

**Cumprimento de Pedidos**
• Processar e cumprir seus pedidos
• Enviar confirmações de pedidos e notificações de envio
• Fornecer suporte ao cliente

**Operações Comerciais**
• Melhorar nosso site e serviços
• Analisar tendências e preferências de uso
• Desenvolver novos produtos e recursos
• Prevenir fraudes e acessos não autorizados

**Comunicações**
• Responder às suas consultas e solicitações
• Enviar atualizações importantes sobre seus pedidos
• Fornecer informações sobre produtos (com seu consentimento)

**Conformidade Legal**
• Cumprir leis e regulamentos aplicáveis
• Responder a solicitações legais e prevenir danos
• Aplicar nossos Termos e Condições`,
        },
        {
          icon: Lock,
          title: 'Segurança da Informação',
          content: `Implementamos medidas técnicas e organizacionais apropriadas para proteger suas informações pessoais:

**Medidas de Segurança**
• Criptografia SSL/TLS para transmissão de dados
• Processamento seguro de pagamentos através de provedores compatíveis com PCI
• Controles de acesso e autenticação
• Avaliações regulares de segurança
• Treinamento de funcionários sobre proteção de dados

**Retenção de Dados**
Retemos suas informações pessoais apenas pelo tempo necessário para:
• Cumprir os propósitos descritos nesta política
• Cumprir obrigações legais
• Resolver disputas e fazer valer acordos

Os registros de pedidos são mantidos por um mínimo de 7 anos para fins legais e contábeis.

**Nota**: Embora nos esforcemos para proteger suas informações, nenhum método de transmissão pela Internet ou armazenamento eletrônico é 100% seguro. Não podemos garantir segurança absoluta.`,
        },
        {
          icon: Globe,
          title: 'Compartilhamento de Informações',
          content: `Não vendemos suas informações pessoais. Podemos compartilhar suas informações com:

**Provedores de Serviços**
• Processadores de pagamento
• Empresas de envio e logística
• Provedores de TI e hospedagem
• Serviços de análise

**Requisitos Legais**
Podemos divulgar suas informações se exigido por lei ou em resposta a:
• Ordens judiciais ou processos legais
• Solicitações governamentais
• Proteção de nossos direitos e propriedade
• Prevenção de fraudes ou atividades ilegais

**Transferências Comerciais**
Em caso de fusão, aquisição ou venda de ativos, suas informações podem ser transferidas como parte dessa transação.`,
        },
        {
          icon: UserCheck,
          title: 'Seus Direitos',
          content: `Dependendo de sua localização, você pode ter os seguintes direitos em relação às suas informações pessoais:

**Acesso e Portabilidade**
• Solicitar uma cópia de seus dados pessoais
• Receber seus dados em formato portátil

**Correção e Exclusão**
• Solicitar correção de dados imprecisos
• Solicitar exclusão de seus dados (sujeito a obrigações legais)

**Direitos de Opt-Out**
• Cancelar inscrição em comunicações de marketing
• Desativar cookies através das configurações do navegador
• Optar por não participar do rastreamento de análise

**Exercer Seus Direitos**
Para exercer qualquer um desses direitos, entre em contato conosco em support@pep-t.com. Responderemos à sua solicitação em até 30 dias.`,
        },
        {
          icon: Bell,
          title: 'Comunicações e Marketing',
          content: `**Comunicações Transacionais**
Enviaremos e-mails relacionados aos seus pedidos, incluindo:
• Confirmações de pedidos
• Atualizações de envio
• Notificações importantes da conta

Essas comunicações são necessárias para o cumprimento do pedido e não podem ser canceladas.

**Comunicações de Marketing**
Com seu consentimento, podemos enviar e-mails promocionais sobre:
• Novos produtos
• Ofertas especiais
• Notícias do setor

Você pode cancelar a inscrição em e-mails de marketing a qualquer momento:
• Clicando no link "cancelar inscrição" em qualquer e-mail
• Entrando em contato conosco em support@pep-t.com`,
        },
        {
          icon: Shield,
          title: 'Transferências Internacionais de Dados',
          content: `Como uma empresa exclusivamente de exportação, suas informações podem ser transferidas e processadas em:
• Estados Unidos (onde nossos servidores estão localizados)
• Países onde nossos provedores de serviços operam

Garantimos que salvaguardas apropriadas estejam em vigor para transferências internacionais, incluindo:
• Cláusulas contratuais padrão
• Acordos de proteção de dados
• Conformidade com leis de proteção de dados aplicáveis

Ao usar nosso site e serviços, você consente com a transferência de suas informações para esses países.`,
        },
      ],
      children: {
        title: 'Privacidade de Menores',
        content: 'Nosso site e serviços não são destinados a indivíduos menores de 21 anos. Não coletamos intencionalmente informações pessoais de menores. Se tomarmos conhecimento de que coletamos informações de uma pessoa menor de 21 anos, excluiremos essas informações prontamente.',
      },
      changes: {
        title: 'Alterações nesta Política',
        content: 'Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos você sobre quaisquer alterações materiais publicando a nova política nesta página e atualizando a data de "Última Atualização". Seu uso continuado de nosso site após tais alterações constitui aceitação da política atualizada.',
      },
      contact: {
        title: 'Entre em Contato',
        text: 'Se você tiver dúvidas sobre esta Política de Privacidade ou nossas práticas de dados, entre em contato:',
        email: 'support@pep-t.com',
        location: 'Boca Raton, Flórida, EUA',
      },
    },
    es: {
      title: 'Política de Privacidad',
      lastUpdated: 'Última Actualización: Diciembre 2024',
      intro: 'PEP-T.COM ("nosotros", "nos" o "nuestro") está comprometido a proteger su privacidad. Esta Política de Privacidad explica cómo recopilamos, usamos, divulgamos y protegemos su información cuando visita nuestro sitio web y compra nuestros productos.',
      sections: [
        {
          icon: Database,
          title: 'Información que Recopilamos',
          content: `**Información Personal**
Cuando visita nuestro sitio web o realiza una compra, podemos recopilar la siguiente información personal:

• Nombre y datos de contacto (dirección de correo electrónico, número de teléfono)
• Dirección de facturación y envío
• Nombre de la empresa o institución
• Información de pago (procesada de forma segura por procesadores de pago de terceros)
• Historial de pedidos y preferencias

**Información Recopilada Automáticamente**
Recopilamos automáticamente cierta información cuando visita nuestro sitio web:

• Dirección IP y ubicación geográfica
• Tipo y versión del navegador
• Sistema operativo
• Páginas vistas y tiempo en el sitio
• Direcciones de sitios de referencia
• Información del dispositivo

**Cookies y Tecnologías de Seguimiento**
Usamos cookies y tecnologías similares para:

• Recordar sus preferencias y configuraciones
• Analizar el tráfico y patrones de uso del sitio
• Mejorar la funcionalidad del sitio
• Proporcionar contenido personalizado`,
        },
        {
          icon: Eye,
          title: 'Cómo Usamos Su Información',
          content: `Usamos la información recopilada para los siguientes propósitos:

**Cumplimiento de Pedidos**
• Procesar y cumplir sus pedidos
• Enviar confirmaciones de pedidos y notificaciones de envío
• Proporcionar soporte al cliente

**Operaciones Comerciales**
• Mejorar nuestro sitio web y servicios
• Analizar tendencias y preferencias de uso
• Desarrollar nuevos productos y funciones
• Prevenir fraudes y accesos no autorizados

**Comunicaciones**
• Responder a sus consultas y solicitudes
• Enviar actualizaciones importantes sobre sus pedidos
• Proporcionar información sobre productos (con su consentimiento)

**Cumplimiento Legal**
• Cumplir con las leyes y regulaciones aplicables
• Responder a solicitudes legales y prevenir daños
• Aplicar nuestros Términos y Condiciones`,
        },
        {
          icon: Lock,
          title: 'Seguridad de la Información',
          content: `Implementamos medidas técnicas y organizativas apropiadas para proteger su información personal:

**Medidas de Seguridad**
• Cifrado SSL/TLS para transmisión de datos
• Procesamiento seguro de pagos a través de proveedores compatibles con PCI
• Controles de acceso y autenticación
• Evaluaciones regulares de seguridad
• Capacitación de empleados sobre protección de datos

**Retención de Datos**
Retenemos su información personal solo durante el tiempo necesario para:
• Cumplir los propósitos descritos en esta política
• Cumplir obligaciones legales
• Resolver disputas y hacer cumplir acuerdos

Los registros de pedidos se mantienen por un mínimo de 7 años para fines legales y contables.

**Nota**: Aunque nos esforzamos por proteger su información, ningún método de transmisión por Internet o almacenamiento electrónico es 100% seguro. No podemos garantizar seguridad absoluta.`,
        },
        {
          icon: Globe,
          title: 'Compartir Información',
          content: `No vendemos su información personal. Podemos compartir su información con:

**Proveedores de Servicios**
• Procesadores de pagos
• Empresas de envío y logística
• Proveedores de TI y alojamiento
• Servicios de análisis

**Requisitos Legales**
Podemos divulgar su información si lo exige la ley o en respuesta a:
• Órdenes judiciales o procesos legales
• Solicitudes gubernamentales
• Protección de nuestros derechos y propiedad
• Prevención de fraudes o actividades ilegales

**Transferencias Comerciales**
En caso de fusión, adquisición o venta de activos, su información puede ser transferida como parte de esa transacción.`,
        },
        {
          icon: UserCheck,
          title: 'Sus Derechos',
          content: `Dependiendo de su ubicación, puede tener los siguientes derechos respecto a su información personal:

**Acceso y Portabilidad**
• Solicitar una copia de sus datos personales
• Recibir sus datos en formato portátil

**Corrección y Eliminación**
• Solicitar corrección de datos inexactos
• Solicitar eliminación de sus datos (sujeto a obligaciones legales)

**Derechos de Exclusión**
• Cancelar suscripción de comunicaciones de marketing
• Desactivar cookies a través de la configuración del navegador
• Optar por no participar en el seguimiento de análisis

**Ejercer Sus Derechos**
Para ejercer cualquiera de estos derechos, contáctenos en support@pep-t.com. Responderemos a su solicitud dentro de 30 días.`,
        },
        {
          icon: Bell,
          title: 'Comunicaciones y Marketing',
          content: `**Comunicaciones Transaccionales**
Le enviaremos correos electrónicos relacionados con sus pedidos, incluyendo:
• Confirmaciones de pedidos
• Actualizaciones de envío
• Notificaciones importantes de la cuenta

Estas comunicaciones son necesarias para el cumplimiento del pedido y no se pueden cancelar.

**Comunicaciones de Marketing**
Con su consentimiento, podemos enviar correos promocionales sobre:
• Nuevos productos
• Ofertas especiales
• Noticias de la industria

Puede cancelar la suscripción de correos de marketing en cualquier momento:
• Haciendo clic en el enlace "cancelar suscripción" en cualquier correo
• Contactándonos en support@pep-t.com`,
        },
        {
          icon: Shield,
          title: 'Transferencias Internacionales de Datos',
          content: `Como empresa exclusivamente de exportación, su información puede ser transferida y procesada en:
• Estados Unidos (donde están ubicados nuestros servidores)
• Países donde operan nuestros proveedores de servicios

Aseguramos que existan salvaguardas apropiadas para transferencias internacionales, incluyendo:
• Cláusulas contractuales estándar
• Acuerdos de protección de datos
• Cumplimiento con las leyes de protección de datos aplicables

Al usar nuestro sitio web y servicios, usted consiente la transferencia de su información a estos países.`,
        },
      ],
      children: {
        title: 'Privacidad de Menores',
        content: 'Nuestro sitio web y servicios no están destinados a personas menores de 21 años. No recopilamos intencionalmente información personal de menores. Si nos damos cuenta de que hemos recopilado información de una persona menor de 21 años, eliminaremos esa información de inmediato.',
      },
      changes: {
        title: 'Cambios en esta Política',
        content: 'Podemos actualizar esta Política de Privacidad periódicamente. Le notificaremos sobre cualquier cambio material publicando la nueva política en esta página y actualizando la fecha de "Última Actualización". Su uso continuado de nuestro sitio web después de dichos cambios constituye la aceptación de la política actualizada.',
      },
      contact: {
        title: 'Contáctenos',
        text: 'Si tiene preguntas sobre esta Política de Privacidad o nuestras prácticas de datos, contáctenos:',
        email: 'support@pep-t.com',
        location: 'Boca Raton, Florida, EE.UU.',
      },
    },
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-neutral-50 pt-20 lg:pt-24" data-testid="privacy-page">
      {/* Header */}
      <div className="bg-neutral-900 text-white py-16">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-red-500" />
            <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t.title}
            </h1>
          </div>
          <p className="text-neutral-400">{t.lastUpdated}</p>
        </div>
      </div>

      <div className="container-custom py-12">
        {/* Introduction */}
        <div className="bg-white border border-neutral-200 p-6 mb-8 rounded-sm">
          <p className="text-neutral-700 leading-relaxed">{t.intro}</p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {t.sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div key={index} className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
                <div className="bg-neutral-50 border-b border-neutral-200 px-6 py-4 flex items-center gap-3">
                  <div className="p-2 bg-red-50 rounded-sm">
                    <Icon className="w-5 h-5 text-red-600" />
                  </div>
                  <h2 className="text-xl font-bold text-neutral-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {section.title}
                  </h2>
                </div>
                <div className="p-6">
                  <div className="prose prose-neutral max-w-none">
                    {section.content.split('\n\n').map((paragraph, pIndex) => (
                      <div key={pIndex} className="mb-4 last:mb-0">
                        {paragraph.startsWith('**') ? (
                          <p className="whitespace-pre-wrap text-neutral-700 leading-relaxed" 
                             dangerouslySetInnerHTML={{
                               __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong class="text-neutral-900">$1</strong>')
                             }} 
                          />
                        ) : paragraph.startsWith('•') ? (
                          <ul className="list-disc list-inside space-y-1 text-neutral-700">
                            {paragraph.split('\n').map((item, iIndex) => (
                              <li key={iIndex}>{item.replace('• ', '')}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-neutral-700 leading-relaxed whitespace-pre-wrap"
                             dangerouslySetInnerHTML={{
                               __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong class="text-neutral-900">$1</strong>')
                             }} 
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Children's Privacy */}
        <div className="bg-amber-50 border border-amber-200 p-6 mt-8 rounded-sm">
          <h3 className="text-lg font-bold text-amber-900 mb-3">{t.children.title}</h3>
          <p className="text-amber-800">{t.children.content}</p>
        </div>

        {/* Changes */}
        <div className="bg-white border border-neutral-200 p-6 mt-8 rounded-sm">
          <h3 className="text-lg font-bold text-neutral-900 mb-3">{t.changes.title}</h3>
          <p className="text-neutral-700">{t.changes.content}</p>
        </div>

        {/* Contact */}
        <div className="bg-neutral-900 text-white p-6 mt-8 rounded-sm">
          <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
            <Mail className="w-5 h-5 text-red-400" />
            {t.contact.title}
          </h3>
          <p className="text-neutral-300 mb-4">{t.contact.text}</p>
          <div className="space-y-2">
            <p className="text-neutral-200">
              <span className="text-red-400">Email:</span> {t.contact.email}
            </p>
            <p className="text-neutral-200">
              <span className="text-red-400">{language === 'pt' ? 'Localização:' : 'Location:'}</span> {t.contact.location}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
